import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { API_BASE } from '../../config/api';

const PageSection = styled(motion.section)`
  min-height: 100vh;
  padding: 150px 2rem 4rem;
  max-width: 480px;
  margin: 0 auto;
`;

const Card = styled.div`
  background: #1a1a1a;
  border-radius: 10px;
  padding: 2rem;
  color: #ccc;
`;

const Title = styled.h2`
  color: #fff;
  margin-bottom: 0.75rem;
  font-size: 1.35rem;
`;

const Input = styled.input`
  width: 100%;
  box-sizing: border-box;
  padding: 0.65rem 0.85rem;
  border-radius: 6px;
  border: 1px solid #333;
  background: #0f0f0f;
  color: #eee;
  font-size: 0.95rem;
  margin-bottom: 0.75rem;
`;

const Btn = styled(motion.button)`
  width: 100%;
  padding: 0.75rem;
  border-radius: 6px;
  border: none;
  cursor: pointer;
  background: #2d6a4f;
  color: #fff;
  font-size: 1rem;
  margin-top: 0.5rem;

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const ErrorBox = styled.div`
  color: #ff6b6b;
  padding: 0.75rem;
  margin-top: 1rem;
  border-radius: 6px;
  background: rgba(255, 68, 68, 0.08);
  font-size: 0.9rem;
`;

const OkBox = styled.div`
  color: #86efac;
  padding: 0.75rem;
  margin-top: 1rem;
  border-radius: 6px;
  background: rgba(34, 197, 94, 0.1);
  font-size: 0.9rem;
`;

const AnalyticsBootstrap = () => {
  const [email, setEmail] = useState('');
  const [secret, setSecret] = useState('');
  const [msg, setMsg] = useState('');
  const [err, setErr] = useState('');
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setErr('');
    setMsg('');
    try {
      const res = await fetch(`${API_BASE}/api/analytics/bootstrap-first-admin`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: email.trim(),
          secret,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data.error || `Request failed (${res.status})`);
      }
      setMsg(
        `Done. Register or log in as ${data.email}, then open Analytics from the header.`
      );
      setSecret('');
    } catch (e) {
      setErr(e.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <PageSection initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}>
      <Card>
        <Title>First analytics admin</Title>
        <p style={{ fontSize: '0.9rem', marginBottom: '1.25rem', lineHeight: 1.5 }}>
          Use this once when the database has no admin emails yet. The worker must have the
          secret{' '}
          <code style={{ color: '#94a3b8', fontSize: '0.85rem' }}>
            ANALYTICS_ADMIN_BOOTSTRAP_SECRET
          </code>{' '}
          set. After that, add more people from Analytics → Team.
        </p>
        <p style={{ fontSize: '0.82rem', marginBottom: '1rem', color: '#a8a29e', lineHeight: 1.45 }}>
          If the button returns <strong>401</strong>: the app is calling the Cloudflare API, but that
          deploy does not include this endpoint yet. From the <code style={{ color: '#94a3b8' }}>server</code>{' '}
          folder run <code style={{ color: '#94a3b8' }}>npx wrangler deploy</code>, or create{' '}
          <code style={{ color: '#94a3b8' }}>.env.local</code> in the project root with{' '}
          <code style={{ color: '#94a3b8' }}>REACT_APP_API_URL=http://127.0.0.1:8787</code> and keep{' '}
          <code style={{ color: '#94a3b8' }}>wrangler dev</code> running in <code style={{ color: '#94a3b8' }}>server</code>.
        </p>
        <form onSubmit={submit}>
          <label style={{ fontSize: '0.8rem', color: '#888' }}>Email (must match site login)</label>
          <Input
            type="email"
            autoComplete="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@example.com"
            required
          />
          <label style={{ fontSize: '0.8rem', color: '#888' }}>Bootstrap secret</label>
          <Input
            type="password"
            autoComplete="off"
            value={secret}
            onChange={(e) => setSecret(e.target.value)}
            placeholder="Same value as ANALYTICS_ADMIN_BOOTSTRAP_SECRET"
            required
          />
          <Btn type="submit" disabled={loading} whileTap={{ scale: 0.99 }}>
            {loading ? 'Working…' : 'Add first admin'}
          </Btn>
        </form>
        {err && <ErrorBox>{err}</ErrorBox>}
        {msg && <OkBox>{msg}</OkBox>}
        <p style={{ marginTop: '1.5rem', fontSize: '0.9rem' }}>
          <Link to="/login" style={{ color: '#a78bfa' }}>
            Back to login
          </Link>
        </p>
      </Card>
    </PageSection>
  );
};

export default AnalyticsBootstrap;
